import { useState, useEffect, useRef } from "react";
import {
  Radio, CheckCircle, XCircle, Trash2, Star, ExternalLink,
  Globe, MapPin, Mail, Loader2, Search, Clock, Pencil, Upload, X, Save, Zap, DollarSign, GripVertical, Sparkles
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BACKEND_URL } from "../../context/AuthContext";

const API = BACKEND_URL;

const STATUS_STYLES = {
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-400 border-red-500/20",
};
const STATUS_ICONS = { pending: Clock, approved: CheckCircle, rejected: XCircle };

const GENRE_OPTIONS = [
  "Hip Hop", "R&B", "Pop", "Rock", "Jazz", "Blues", "Country",
  "Electronic", "House", "Reggae", "Latin", "Gospel", "Classical",
  "Afrobeats", "Dancehall", "Soul", "Neo Soul", "Funk", "Metal", "Indie",
  "Talk Radio", "Sports", "News", "Variety", "Other"
];

/* ─── Edit Modal ─── */
const EditModal = ({ listing, authToken, onClose, onSaved }) => {
  const [form, setForm] = useState({
    station_name: listing.station_name || "",
    genre: Array.isArray(listing.genre) ? [...listing.genre] : listing.genre ? [listing.genre] : [],
    stream_url: listing.stream_url || "",
    description: listing.description || "",
    logo_url: listing.logo_url || "",
    location: listing.location || "",
    language: listing.language || "English",
    website: listing.website || "",
    contact_email: listing.contact_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(listing.logo_url || "");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/directory/upload-logo`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      set("logo_url", d.url);
      setLogoPreview(d.url);
    } catch (err) { setError(err.message); }
    setUploading(false);
  };

  const handleSave = async () => {
    setError("");
    if (!form.station_name.trim()) return setError("Station name required");
    if (!form.genre.length) return setError("Select at least one genre");
    if (!form.stream_url.trim()) return setError("Stream URL required");
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/directory/${listing.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Update failed");
      onSaved();
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white text-sm focus:outline-none focus:border-emerald-500/50 placeholder-white/20";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#12121a] border border-white/10 rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg">Edit Station</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white/60 transition"><X className="w-5 h-5" /></button>
        </div>
        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-4 text-red-400 text-xs">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Station Name</label>
            <input value={form.station_name} onChange={e => set("station_name", e.target.value)} className={inputCls} data-testid="edit-name" />
          </div>
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Genre(s)</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {GENRE_OPTIONS.map(g => (
                <button key={g} type="button" onClick={() => set("genre", form.genre.includes(g) ? form.genre.filter(x => x !== g) : [...form.genre, g])}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition ${form.genre.includes(g) ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-white/5 text-white/40 border-white/5 hover:border-white/10"}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Stream URL</label>
            <input value={form.stream_url} onChange={e => set("stream_url", e.target.value)} className={inputCls} data-testid="edit-stream" />
          </div>
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Logo</label>
            <div className="flex items-center gap-3">
              {logoPreview && <img src={logoPreview} alt="" className="w-14 h-14 rounded-lg object-cover bg-white/5" />}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/50 px-3 py-1.5 rounded-lg text-xs transition disabled:opacity-50">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {uploading ? "Uploading..." : "Upload New"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </div>
          </div>
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} className={inputCls + " resize-none"} data-testid="edit-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Location</label>
              <input value={form.location} onChange={e => set("location", e.target.value)} className={inputCls} data-testid="edit-location" />
            </div>
            <div>
              <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Language</label>
              <input value={form.language} onChange={e => set("language", e.target.value)} className={inputCls} data-testid="edit-language" />
            </div>
          </div>
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Website</label>
            <input value={form.website} onChange={e => set("website", e.target.value)} className={inputCls} data-testid="edit-website" />
          </div>
          <div>
            <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">Contact Email</label>
            <input value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className={inputCls} data-testid="edit-contact-email" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/5">
          <button onClick={handleSave} disabled={saving || uploading}
            className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold py-2.5 rounded-lg text-sm transition disabled:opacity-50"
            data-testid="edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 rounded-lg text-sm transition">Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ─── Sortable Listing Row ─── */
const SortableRow = ({ listing, isDraggable, actionLoading, doAction, toggleFeatured, setEditingListing, generateSpotlight, spotlightLoading }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: listing.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : "auto" };

  const StatusIcon = STATUS_ICONS[listing.status] || Clock;
  const genreText = Array.isArray(listing.genre) ? listing.genre.join(", ") : listing.genre || "";

  return (
    <div ref={setNodeRef} style={style} className="bg-white/[0.03] border border-white/5 rounded-xl p-4 hover:border-white/10 transition" data-testid={`listing-${listing.id}`}>
      <div className="flex gap-3">
        {isDraggable && (
          <div {...attributes} {...listeners} className="flex items-center cursor-grab active:cursor-grabbing text-white/20 hover:text-white/50 transition flex-shrink-0 pt-1" data-testid={`drag-handle-${listing.id}`}>
            <GripVertical className="w-5 h-5" />
          </div>
        )}
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
          {listing.logo_url ? <img src={listing.logo_url} alt={listing.station_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            : <div className="w-full h-full flex items-center justify-center"><Radio className="w-6 h-6 text-white/20" /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                {listing.station_name}
                {listing.featured && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                {listing.priority_paid && <span className="flex items-center gap-1 bg-amber-500/20 text-amber-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full"><DollarSign className="w-3 h-3" />PAID</span>}
                {listing.priority && !listing.priority_paid && <span className="flex items-center gap-1 bg-purple-500/20 text-purple-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full"><Zap className="w-3 h-3" />PRIORITY</span>}
              </h3>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                {genreText && <span className="text-white/40 text-xs">{genreText}</span>}
                {listing.location && <span className="flex items-center gap-0.5 text-white/30 text-xs"><MapPin className="w-3 h-3" /> {listing.location}</span>}
                {listing.language && listing.language !== "English" && <span className="flex items-center gap-0.5 text-white/30 text-xs"><Globe className="w-3 h-3" /> {listing.language}</span>}
              </div>
            </div>
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_STYLES[listing.status] || STATUS_STYLES.pending}`}>
              <StatusIcon className="w-3 h-3" />{listing.status}
            </span>
          </div>
          {listing.description && <p className="text-white/30 text-xs mt-1.5 line-clamp-2">{listing.description}</p>}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-xs">
            {listing.stream_url && <a href={listing.stream_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400/60 hover:text-emerald-400 flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> Stream</a>}
            {listing.website && <a href={listing.website} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/50 flex items-center gap-0.5"><Globe className="w-3 h-3" /> Website</a>}
            {listing.contact_email && <span className="text-white/30 flex items-center gap-0.5"><Mail className="w-3 h-3" /> {listing.contact_email}</span>}
            <span className="text-white/20">{listing.submitted_at ? new Date(listing.submitted_at).toLocaleDateString() : ""}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
        {listing.status === "pending" && (
          <>
            <button onClick={() => doAction(listing.id, "approve")} disabled={actionLoading === listing.id}
              className="flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50" data-testid={`approve-${listing.id}`}>
              <CheckCircle className="w-3.5 h-3.5" /> Approve
            </button>
            <button onClick={() => doAction(listing.id, "reject")} disabled={actionLoading === listing.id}
              className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50" data-testid={`reject-${listing.id}`}>
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
        {listing.status === "approved" && (
          <>
            <button onClick={() => toggleFeatured(listing.id)} disabled={actionLoading === listing.id}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50 ${listing.featured ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-amber-400"}`}
              data-testid={`feature-${listing.id}`}>
              <Star className={`w-3.5 h-3.5 ${listing.featured ? "fill-amber-400" : ""}`} /> {listing.featured ? "Unfeature" : "Feature"}
            </button>
            <button onClick={() => generateSpotlight(listing)} disabled={spotlightLoading === listing.id}
              className="flex items-center gap-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 px-3 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50"
              title="Generate AI Station Spotlight article"
              data-testid={`spotlight-${listing.id}`}>
              {spotlightLoading === listing.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {spotlightLoading === listing.id ? "Drafting..." : "Spotlight"}
            </button>
          </>
        )}
        {listing.status === "rejected" && (
          <button onClick={() => doAction(listing.id, "approve")} disabled={actionLoading === listing.id}
            className="flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50" data-testid={`reapprove-${listing.id}`}>
            <CheckCircle className="w-3.5 h-3.5" /> Re-approve
          </button>
        )}
        <button onClick={() => setEditingListing(listing)}
          className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/40 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium transition"
          data-testid={`edit-${listing.id}`}>
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <button onClick={() => { if (window.confirm(`Delete "${listing.station_name}"?`)) doAction(listing.id, "delete"); }}
          disabled={actionLoading === listing.id}
          className="flex items-center gap-1.5 bg-white/5 hover:bg-red-500/10 text-white/30 hover:text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50 ml-auto"
          data-testid={`delete-${listing.id}`}>
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );
};

/* ─── Main Tab ─── */
export default function AdminDirectoryTab({ token }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [actionLoading, setActionLoading] = useState(null);
  const [search, setSearch] = useState("");
  const [editingListing, setEditingListing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [spotlightLoading, setSpotlightLoading] = useState(null);

  const authToken = token || localStorage.getItem("token");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchListings = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/directory/pending`, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await r.json();
      setListings(d.listings || []);
    } catch { setListings([]); }
    setLoading(false);
  };

  useEffect(() => { fetchListings(); }, []);

  const doAction = async (id, action) => {
    setActionLoading(id);
    try {
      const method = action === "delete" ? "DELETE" : "PUT";
      const url = action === "delete" ? `${API}/api/directory/${id}` : `${API}/api/directory/${id}/${action}`;
      await fetch(url, { method, headers: { Authorization: `Bearer ${authToken}` } });
      await fetchListings();
    } catch {}
    setActionLoading(null);
  };

  const toggleFeatured = async (id) => {
    setActionLoading(id);
    try {
      await fetch(`${API}/api/directory/${id}/feature`, { method: "PUT", headers: { Authorization: `Bearer ${authToken}` } });
      await fetchListings();
    } catch {}
    setActionLoading(null);
  };

  const generateSpotlight = async (listing) => {
    if (spotlightLoading) return;
    setSpotlightLoading(listing.id);
    try {
      let r = await fetch(`${API}/api/admin/spotlight/generate/${listing.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      // If a spotlight already exists, confirm then retry with force=true
      if (r.status === 409) {
        const d = await r.json().catch(() => ({}));
        const proceed = window.confirm(
          `${d.detail || "A spotlight already exists for this station."}\n\nGenerate another draft anyway?`
        );
        if (!proceed) { setSpotlightLoading(null); return; }
        r = await fetch(`${API}/api/admin/spotlight/generate/${listing.id}?force=true`, {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        });
      }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Failed (HTTP ${r.status})`);
      const msg = `✨ Spotlight draft created!\n\nTitle: "${d.title}"\nStatus: DRAFT (not published yet)\n\nReview it in: Admin → More → News tab\n\nClick OK to preview now, or Cancel to review later.`;
      if (window.confirm(msg)) {
        window.open(d.preview_url, "_blank", "noopener");
      }
    } catch (e) {
      alert(`Spotlight generation failed: ${e.message}`);
    }
    setSpotlightLoading(null);
  };

  const filtered = listings.filter((l) => {
    if (filter !== "all" && l.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const genreStr = Array.isArray(l.genre) ? l.genre.join(" ") : l.genre || "";
      return l.station_name?.toLowerCase().includes(q) || genreStr.toLowerCase().includes(q) ||
        l.contact_email?.toLowerCase().includes(q) || l.location?.toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    const ao = a.sort_order ?? 9999;
    const bo = b.sort_order ?? 9999;
    if (ao !== bo) return ao - bo;
    const ap = a.priority_paid ? 2 : a.priority ? 1 : 0;
    const bp = b.priority_paid ? 2 : b.priority ? 1 : 0;
    return bp - ap;
  });

  const isDraggable = filter === "approved" && !search;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filtered.findIndex(l => l.id === active.id);
    const newIndex = filtered.findIndex(l => l.id === over.id);
    const reordered = arrayMove(filtered, oldIndex, newIndex);

    // Optimistic update
    const updatedListings = listings.map(l => {
      const idx = reordered.findIndex(r => r.id === l.id);
      return idx >= 0 ? { ...l, sort_order: idx } : l;
    });
    setListings(updatedListings);

    // Save to backend
    setSaving(true);
    try {
      await fetch(`${API}/api/directory/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ ordered_ids: reordered.map(l => l.id) }),
      });
    } catch {}
    setSaving(false);
  };

  const pendingCount = listings.filter((l) => l.status === "pending").length;

  return (
    <div data-testid="admin-directory-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Radio className="w-5 h-5 text-emerald-400" /> Directory Submissions
          {pendingCount > 0 && <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">{pendingCount} pending</span>}
          {saving && <span className="text-white/30 text-xs font-normal ml-2">Saving order...</span>}
        </h2>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input type="text" placeholder="Search submissions..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-emerald-500/50" data-testid="admin-directory-search" />
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex gap-1 bg-white/5 rounded-lg p-1 w-fit" data-testid="admin-directory-filters">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition ${filter === f ? "bg-emerald-500 text-black" : "text-white/50 hover:text-white hover:bg-white/5"}`}
              data-testid={`filter-${f}`}>
              {f}{f === "pending" && pendingCount > 0 && ` (${pendingCount})`}
            </button>
          ))}
        </div>
        {isDraggable && (
          <span className="text-white/20 text-xs flex items-center gap-1"><GripVertical className="w-3 h-3" /> Drag to reorder</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/30"><Radio className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No {filter !== "all" ? filter : ""} submissions found</p></div>
      ) : isDraggable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filtered.map(l => l.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {filtered.map((listing) => (
                <SortableRow key={listing.id} listing={listing} isDraggable actionLoading={actionLoading} doAction={doAction} toggleFeatured={toggleFeatured} setEditingListing={setEditingListing} generateSpotlight={generateSpotlight} spotlightLoading={spotlightLoading} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-3">
          {filtered.map((listing) => (
            <SortableRow key={listing.id} listing={listing} isDraggable={false} actionLoading={actionLoading} doAction={doAction} toggleFeatured={toggleFeatured} setEditingListing={setEditingListing} generateSpotlight={generateSpotlight} spotlightLoading={spotlightLoading} />
          ))}
        </div>
      )}

      {editingListing && (
        <EditModal listing={editingListing} authToken={authToken} onClose={() => setEditingListing(null)}
          onSaved={() => { setEditingListing(null); fetchListings(); }} />
      )}
    </div>
  );
}
